/**
 * FILE: apps/admin/src/components/EditorialErrorState.tsx
 * PURPOSE: Brand-aligned fallback UI for 404, ErrorBoundary, and feature-level error surfaces.
 */

import type { ReactNode } from 'react'
import { withBasePath } from '../lib/appPath'

interface EditorialErrorStateProps {
  /**
   * Mono caps eyebrow displayed above the headline. Use plain English
   * context (e.g. `"404 · not found"`, `"Error · render"`, `"404 · users"`)
   * — the project's Japanese budget per page is one phrase max in body
   * copy, never in eyebrows or headlines (see docs/marketing/VOICE.md).
   */
  eyebrow: string
  /**
   * Headline. The component renders any embedded `<em>` in the
   * vermillion accent so callers can lean on the brand's "one focal
   * vermillion word per editorial unit" pattern (see Hero.tsx).
   */
  headline: ReactNode
  /** Lead copy. Plain text or React for inline `<code>` etc. */
  lead: ReactNode
  /** Optional structured detail (e.g. the path the user typed). */
  detail?: ReactNode
  /**
   * Primary action — defaults to home. Pass `{ to: '/dashboard',
   * label: 'Back to Dashboard' }` for authenticated contexts.
   */
  primary: ActionProps
  /**
   * Secondary action — defaults to opening docs. Optional; pass `null`
   * for boundaries that should only show the primary recovery path.
   */
  secondary?: ActionProps | null
  /**
   * Wrapper element for the editorial layout. Defaults to `'section'`
   * because the most common callers (UsersPage, ReportDetailPage) render
   * this component INSIDE Layout's `<main id="main-content">` — a nested
   * `<main>` would double the page landmark and confuse assistive tech.
   *
   * Pass `'main'` ONLY when the component is the *page-level* root, e.g.
   * the top-level ErrorBoundary fallback (renders before Layout has
   * mounted) or the App.tsx `NotFoundPage` (rendered above the Layout
   * gate for unauthenticated 404s). Doing so makes EditorialErrorState
   * itself the single page landmark in those cases.
   */
  as?: 'main' | 'section'
}

interface ActionProps {
  /** Link target. Same-origin paths render through `<a>` (the boundary
   * does not depend on react-router being mounted, since this component
   * has to work when the app itself crashed). */
  href: string
  label: string
  /** When true, opens in a new tab with safe `rel`. Use for outbound
   * docs / repo links so the visitor's recovery context is preserved. */
  external?: boolean
}

/**
 * Resolve a caller-provided href against the SPA's `BASE_URL` so internal
 * recovery links keep working under a non-root deploy (e.g. the production
 * build at `/mushi-mushi/admin/`).
 *
 * Why: this component intentionally renders `<a>` tags rather than
 * react-router `<Link>`s so it can be used by the top-level error
 * boundary even when the router itself failed to mount. Raw `<a>` tags,
 * however, do NOT respect `BrowserRouter basename={BASE_URL}` — they
 * resolve relative to the document origin. So a "Back to reports" with
 * `href="/reports"` from `https://kensaur.us/mushi-mushi/admin/reports/x`
 * was navigating the visitor to `https://kensaur.us/reports` — a 404 at
 * the apex domain. Vite injects `BASE_URL` as a build-time string so we
 * can prepend it without depending on any runtime React context.
 *
 * Absolute `https://…` URLs (and any caller marked `external`) are
 * passed through untouched.
 */
function resolveHref(href: string, external?: boolean): string {
  return external ? href : withBasePath(href)
}

function Action({ href, label, external, kind }: ActionProps & { kind: 'primary' | 'secondary' }) {
  const base =
    'inline-flex items-center gap-2 rounded-md px-4 py-2.5 font-mono text-3xs font-semibold uppercase tracking-[0.18em] transition motion-safe:hover:-translate-y-0.5'
  const tone =
    kind === 'primary'
      ? 'bg-editorial-ink text-editorial-paper shadow-[inset_0_-2px_0_rgba(255,255,255,0.18)] hover:bg-[color-mix(in_oklch,var(--color-editorial-ink)_82%,var(--color-editorial-vermillion))]'
      : 'border border-editorial-rule bg-editorial-paper text-editorial-ink hover:border-editorial-ink hover:bg-editorial-paper-wash'
  return (
    <a
      href={resolveHref(href, external)}
      className={`${base} ${tone}`}
      {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
    >
      {label}
    </a>
  )
}

export function EditorialErrorState({
  eyebrow,
  headline,
  lead,
  detail,
  primary,
  secondary,
  as = 'section',
}: EditorialErrorStateProps) {
  const Wrapper = as
  return (
    <Wrapper className="mushi-marketing-surface grid min-h-[70vh] place-items-center px-6 py-16">
      <article className="mx-auto w-full max-w-[38rem]">
        <p
          aria-hidden="true"
          className="mb-5 inline-flex items-center gap-2.5 font-mono text-3xs font-semibold uppercase tracking-[0.22em] text-editorial-ink-muted"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-editorial-vermillion" />
          {eyebrow}
        </p>
        <h1 className="font-serif text-[clamp(2rem,4.5vw,3rem)] font-semibold leading-[1.05] tracking-[-0.03em] text-editorial-ink [&_em]:not-italic [&_em]:font-bold [&_em]:text-editorial-vermillion">
          {headline}
        </h1>
        <div
          aria-hidden="true"
          className="my-6 h-px"
          style={{
            background:
              'linear-gradient(90deg, var(--color-editorial-vermillion) 0, var(--color-editorial-vermillion) 3rem, var(--color-editorial-rule) 3rem)',
          }}
        />
        <p className="max-w-[54ch] text-[1.0625rem] leading-relaxed text-editorial-ink-muted">
          {lead}
        </p>
        {detail ? (
          <div className="mt-3 font-mono text-xs text-editorial-ink-muted">
            {detail}
          </div>
        ) : null}
        <div className="mt-7 flex flex-wrap gap-3">
          <Action {...primary} kind="primary" />
          {secondary ? <Action {...secondary} kind="secondary" /> : null}
        </div>
      </article>
    </Wrapper>
  )
}
